import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultTheme, useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { JobInfo } from '../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { formattedDateTime } from '../../../utils/dateUtils';
import { sessionStorageUtils } from '../../../utils/session-storage-utils';
import { textUtils } from '../../../utils/text/text';
import { isDefined, isIncluded } from '../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { Button } from '../button/button';
import { DataTable, DataTableSortType } from '../data-table/data-table';
import { Icon } from '../icon/icon';
import { InfoBox } from '../info-box/info-box';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';

const pageSize = 15;

async function getBatchJobs(): Promise<JobInfo[]> {
  const response = await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().fetchJobs({});

  return response.jobs;
}

type Item = JobInfo & {
  durationMs: number;
  statusIcon: ReactNode;
  projectButton: ReactNode;
  geodataName: string;
};

function mapToItem(jobInfo: JobInfo, theme: DefaultTheme): Item {
  const item: Item = {
    ...jobInfo,
    durationMs: isDefined(jobInfo.startTime)
      ? (jobInfo.stopTime ?? Date.now()) - jobInfo.startTime
      : 0,
    statusIcon: BatchJobStatusIcon(jobInfo, theme),
    projectButton: <ProjectButton jobInfo={jobInfo} />,
    geodataName: jobInfo.geodataName ?? '-- manuell --',
  };

  return item;
}

const ProjectButton = ({ jobInfo }: { jobInfo: JobInfo }) => {
  const navigate = useNavigate();
  return (
    <Button
      leftIcon={{ icon: ['fad', 'globe-pointer'] }}
      padding={'0.1em'}
      variant="text"
      onClick={(e) => {
        e.stopPropagation();
        if (isDefined(jobInfo.projectId)) {
          void navigate(`/projects/${jobInfo.projectId}`);
        } else {
          publish(SubscriptionTopic.ToastrMessage, {
            message: 'Okänt projekt.',
            type: 'error',
          });
        }
      }}
    >
      {jobInfo.projectName ?? '---'}
    </Button>
  );
};

function useBatchJobListQuery() {
  const theme = useTheme();
  const { handleError, hasError, clearErrors, buildErrorList } = useErrorHandling();

  const { data, refetch, isPending, isRefetching, error } = useQuery({
    queryKey: ['getBatchJobs'],
    queryFn: getBatchJobs,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Define effect on error outside of mutation to avoid infinite render loop.
  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: refetch });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error, refetch]);

  const sortedJobs = useMemo(() => {
    return (data?.map((x) => mapToItem(x, theme)) ?? []).sort(
      (a, b) => (b.createdTime ?? 0) - (a.createdTime ?? 0),
    );
  }, [data, theme]);

  return {
    jobs: sortedJobs,
    refetch,
    buildErrorList,
    hasError,
    isPending: isPending || isRefetching,
  };
}

function formatDate(value?: number): string {
  if (!isDefined(value)) {
    return '-';
  }
  return formattedDateTime(value);
}

function formatDurationMs(milliseconds: number): string {
  if (milliseconds === 0) {
    return '-';
  }

  const hours = Math.floor(milliseconds / 1000 / 60 / 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const seconds = Math.floor((milliseconds / 1000) % 60);

  const parts = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (hours === 0 && seconds > 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
}

const pendingBatchJobStatuses = [
  'RUNNING',
  'RUNNABLE',
  'PENDING',
  'STARTING',
  'SUBMITTED',
] as const;
type BatchJobStatus = (typeof pendingBatchJobStatuses)[number] | 'SUCCEEDED' | 'FAILED';

function resoveStatusIconColor(status: BatchJobStatus | undefined, theme: DefaultTheme) {
  if (status === 'SUCCEEDED') {
    return theme.color.green;
  }

  if (status === 'FAILED') {
    return theme.color.red;
  }

  return theme.color.gray.medium;
}

function resolveStatusIconName(status?: BatchJobStatus): IconName {
  if (status === 'SUCCEEDED') {
    return 'check';
  }

  if (status === 'FAILED') {
    return 'exclamation-circle';
  }

  if (isIncluded(pendingBatchJobStatuses, status)) {
    return 'spinner';
  }

  return 'question-circle';
}

function BatchJobStatusIcon(jobInfo: JobInfo, theme: DefaultTheme): ReactNode {
  const status = jobInfo.status as BatchJobStatus | undefined;
  return (
    <Icon
      color={resoveStatusIconColor(status, theme)}
      icon={['fad', resolveStatusIconName(status)]}
      key={v4()}
      size="lg"
      spin={isIncluded(pendingBatchJobStatuses, status)}
      title={`status: ${status ?? 'unknown'}, reason: ${jobInfo.reason ?? '---'}`}
    />
  );
}

const searchKeys = [
  'projectName',
  'companyName',
  'geodataName',
  'name',
  'status',
  'reason',
] as const;
function filterItems(items: Item[], searchText: string) {
  const text = searchText.trim().toLowerCase();
  if (text.length === 0) {
    return items;
  }

  return items.filter((item) =>
    searchKeys.some((x) => item[x]?.toLocaleLowerCase().includes(text)),
  );
}

export const BatchJobList = () => {
  const { jobs, buildErrorList, isPending, hasError, refetch } = useBatchJobListQuery();
  const [updated, setUpdated] = useState(new Date());
  const [items, setItems] = useState<Item[]>([]);
  const [searchText, setSearchText] = useState(
    sessionStorageUtils.getUserItemOrDefault('batchJobListSearchText', ''),
  );

  useEffect(() => {
    setItems(filterItems(jobs, searchText));
  }, [searchText, jobs]);

  if (hasError()) {
    return <>{buildErrorList()}</>;
  }

  return (
    <OverlayLoader visible={isPending}>
      <Stack spacing={1}>
        <InfoBox color="yellow" width="fit-content">
          <Stack spacing={1}>
            <span>
              Listar de senaste uppladdningarna för SkyView och molnprocesseringarna från AWS Batch.
            </span>
            <span>
              Notera att AWS börjar rensa upp Batch-loggarna 24 timmar efter de fått status
              SUCCEEDED eller FAILED. Jobb som är äldre än så kan saknas. Håll musen över ikonen för
              att se status.
            </span>
            <Button
              color="secondary"
              leftIcon={{ icon: ['fad', 'refresh'] }}
              variant="contained"
              width="fit-content"
              onClick={() => {
                void refetch().then(() => {
                  setUpdated(new Date());
                });
              }}
            >
              Hämta igen
            </Button>
            <span>(Uppdaterad: {format(updated, 'HH:mm:ss')})</span>
          </Stack>
        </InfoBox>
        <TextBox
          placeholder="Sök"
          value={searchText}
          width={350}
          onChange={(e) => {
            setSearchText(e.target.value);
            sessionStorageUtils.setUserItem('batchJobListSearchText', e.target.value);
          }}
        />
        <DataTable
          columns={{
            statusIcon: {
              title: 'Status',
              sortableBy: { type: DataTableSortType.STRING, columnKey: 'status' },
              unfilterable: true,
              alignment: 'center',
            },
            name: {
              sortableBy: { type: DataTableSortType.STRING },
              unfilterable: true,
              title: 'Namn',
            },
            geodataName: {
              sortableBy: { type: DataTableSortType.STRING },
              unfilterable: true,
              title: 'Molnprocessering',
            },
            projectButton: {
              alignment: 'left',
              sortableBy: { type: DataTableSortType.STRING, columnKey: 'projectName' },
              unfilterable: true,
              title: 'Projekt',
            },
            companyName: {
              sortableBy: { type: DataTableSortType.STRING },
              unfilterable: true,
              title: 'Företag',
            },
            createdTime: {
              sortableBy: { type: DataTableSortType.NUMBER },
              unfilterable: true,
              formatter: formatDate,
              title: 'Skapad',
            },
            durationMs: {
              sortableBy: { type: DataTableSortType.NUMBER },
              formatter: formatDurationMs,
              unfilterable: true,
              title: 'Körtid',
            },
          }}
          fixedLayout={false}
          items={items}
          pageSize={pageSize}
          tableStyle={{
            rowCell: {
              fontSize: '14px',
            },
            headerCell: {
              fontSize: '14px',
            },
          }}
          onRowClicked={(item) => {
            textUtils.copyTextToClipboard(
              JSON.stringify({
                durationMs: formatDurationMs(item.durationMs),
                startTime: formatDate(item.startTime),
                stopTime: formatDate(item.stopTime),
                name: item.name,
                companyName: item.companyName,
                createdTime: item.createdTime,
                geodataId: item.geodataId,
                geodataName: item.geodataName,
                projectId: item.projectId,
                projectName: item.projectName,
                reason: item.reason,
                status: item.status,
              }),
            );
            publish(SubscriptionTopic.ToastrMessage, {
              message: 'Kopierade jobbet',
              type: 'success',
            });
          }}
        />
      </Stack>
    </OverlayLoader>
  );
};
